import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, InputGroup, Badge, ListGroup, Row, Col, Spinner } from 'react-bootstrap';
import { FaRobot, FaCircle, FaPaperPlane, FaUser } from 'react-icons/fa';

interface ChatMessage {
  id: string;
  type: 'user' | 'agent';
  text: string;
  timestamp: Date;
  agentId?: string;
  metadata?: any;
}

interface Agent {
  id: string;
  name: string;
  status: 'online' | 'busy' | 'offline';
  capabilities: string[];
  lastResponse?: Date;
}

interface Conversation {
  agentId: string;
  messages: ChatMessage[];
  conversationId?: string;
  suggestions?: string[];
}

interface AgentChatProps {
  aiOpsAPI: any;
}

const AgentChat: React.FC<AgentChatProps> = ({ aiOpsAPI }) => {
  const [agents, setAgents] = useState<Agent[]>([
    { id: 'otis', name: 'OTIS-Orchestrator', status: 'online', capabilities: ['orchestration', 'workflow', 'status'] },
    { id: 'supervisor-prime', name: 'SupervisorPrime-Ω', status: 'online', capabilities: ['management', 'agent-control', 'reporting'] },
    { id: 'claude-daemon', name: 'ClaudeDaemon-Service', status: 'online', capabilities: ['analysis', 'optimization', 'planning'] },
    { id: 'docmaster-sigma', name: 'DocMaster-Sigma', status: 'online', capabilities: ['documents', 'processing', 'indexing'] },
    { id: 'compliance-phi', name: 'ComplianceAnalyst-Phi', status: 'online', capabilities: ['compliance', 'review', 'validation'] }
  ]);

  const [selectedAgent, setSelectedAgent] = useState<string>('otis');
  const [conversations, setConversations] = useState<Map<string, Conversation>>(new Map());
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  
  const chatBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize conversations for quick access agents
    const quickAccessAgents = ['otis', 'supervisor-prime', 'claude-daemon'];
    quickAccessAgents.forEach(agentId => {
      if (!conversations.has(agentId)) {
        conversations.set(agentId, {
          agentId,
          messages: [],
          suggestions: getDefaultSuggestions(agentId)
        });
      }
    });
    setConversations(new Map(conversations));
    
    // Set initial suggestions
    setSuggestions(getDefaultSuggestions(selectedAgent));
  }, []);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [conversations, selectedAgent]);

  const getDefaultSuggestions = (agentId: string): string[] => {
    switch (agentId) {
      case 'otis':
        return [
          'Show me current system status',
          'What workflows are running?',
          'Any alerts I should know about?'
        ];
      case 'supervisor-prime':
        return [
          'Status report',
          'Start workflow: document processing',
          'Show agent utilization'
        ];
      case 'claude-daemon':
        return [
          'Analyze recent error patterns',
          'Suggest optimizations',
          'Generate deployment plan'
        ];
      default:
        return [
          'What is your current status?',
          'Show recent activities',
          'Help'
        ];
    }
  };

  const sendMessage = async () => {
    if (!currentMessage.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      text: currentMessage,
      timestamp: new Date()
    };

    // Add user message to conversation
    const conversation = conversations.get(selectedAgent) || { 
      agentId: selectedAgent, 
      messages: [],
      suggestions: getDefaultSuggestions(selectedAgent)
    };
    
    conversation.messages.push(userMessage);
    setConversations(new Map(conversations.set(selectedAgent, conversation)));
    setCurrentMessage('');
    setIsLoading(true);

    try {
      const response = await aiOpsAPI.post('/api/agents/communicate', {
        agentId: selectedAgent,
        message: currentMessage,
        conversationId: conversation.conversationId
      });

      const agentMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'agent',
        text: response.data.response,
        timestamp: new Date(),
        agentId: selectedAgent,
        metadata: response.data.metadata
      };

      conversation.messages.push(agentMessage);
      conversation.conversationId = response.data.conversationId;
      conversation.suggestions = response.data.suggestions || getDefaultSuggestions(selectedAgent);
      
      setConversations(new Map(conversations.set(selectedAgent, conversation)));
      setSuggestions(conversation.suggestions || []);

    } catch (error) {
      console.error('Error communicating with agent:', error);
      
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'agent',
        text: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
        agentId: selectedAgent
      };
      
      conversation.messages.push(errorMessage);
      setConversations(new Map(conversations.set(selectedAgent, conversation)));
    }

    setIsLoading(false);
  };

  const handleAgentChange = (agentId: string) => {
    setSelectedAgent(agentId);
    const conversation = conversations.get(agentId);
    setSuggestions(conversation?.suggestions || getDefaultSuggestions(agentId));
  };

  const handleSuggestionClick = (suggestion: string) => {
    setCurrentMessage(suggestion);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'busy': return 'warning';
      case 'offline': return 'danger';
      default: return 'secondary';
    }
  };

  const currentConversation = conversations.get(selectedAgent) || { 
    agentId: selectedAgent, 
    messages: [] 
  };

  return (
    <Card className="h-100" style={{ minHeight: '600px' }}>
      <Card.Header className="bg-dark text-white">
        <h5 className="mb-0">
          <FaRobot className="me-2" />
          Agent Communication Center
        </h5>
      </Card.Header>

      <Card.Body className="p-0 d-flex" style={{ height: 'calc(100% - 56px)' }}>
        <div className="border-end" style={{ width: '250px', overflowY: 'auto' }}>
          <div className="p-3">
            <h6 className="text-muted mb-3">Quick Access</h6>
            <ListGroup variant="flush">
              {agents.filter(a => ['otis', 'supervisor-prime', 'claude-daemon'].includes(a.id)).map(agent => (
                <ListGroup.Item
                  key={agent.id}
                  action
                  active={selectedAgent === agent.id}
                  onClick={() => handleAgentChange(agent.id)}
                  className="d-flex align-items-center justify-content-between"
                >
                  <div>
                    <div className="fw-bold">{agent.name}</div>
                    <small className="text-muted">{agent.capabilities.join(', ')}</small>
                  </div>
                  <FaCircle size={10} className={`text-${getStatusColor(agent.status)}`} />
                </ListGroup.Item>
              ))}
            </ListGroup>

            <h6 className="text-muted mb-3 mt-4">All Agents</h6>
            <ListGroup variant="flush">
              {agents.filter(a => !['otis', 'supervisor-prime', 'claude-daemon'].includes(a.id)).map(agent => (
                <ListGroup.Item
                  key={agent.id}
                  action
                  active={selectedAgent === agent.id}
                  onClick={() => handleAgentChange(agent.id)}
                  className="d-flex align-items-center justify-content-between"
                >
                  <div>
                    <div className="fw-bold">{agent.name}</div>
                  </div>
                  <FaCircle size={10} className={`text-${getStatusColor(agent.status)}`} />
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        </div>

        <div className="flex-grow-1 d-flex flex-column">
          <div className="p-3 border-bottom bg-light">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h6 className="mb-0">
                  {agents.find(a => a.id === selectedAgent)?.name || selectedAgent}
                </h6>
                <small className="text-muted">
                  {agents.find(a => a.id === selectedAgent)?.capabilities.join(' • ')}
                </small>
              </div>
              <Badge bg={getStatusColor(agents.find(a => a.id === selectedAgent)?.status || 'offline')}>
                {agents.find(a => a.id === selectedAgent)?.status || 'offline'}
              </Badge>
            </div>
          </div>

          <div 
            ref={chatBodyRef}
            className="flex-grow-1 p-3" 
            style={{ 
              overflowY: 'auto',
              backgroundColor: '#f8f9fa'
            }}
          >
            {currentConversation.messages.length === 0 ? (
              <div className="text-center text-muted py-5">
                <FaRobot size={48} className="mb-3" />
                <p>Start a conversation with {agents.find(a => a.id === selectedAgent)?.name}</p>
              </div>
            ) : (
              currentConversation.messages.map(message => (
                <div
                  key={message.id}
                  className={`mb-3 d-flex ${message.type === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                >
                  <div
                    className={`p-3 rounded ${message.type === 'user' ? 'bg-primary text-white' : 'bg-white border'}`}
                    style={{ maxWidth: '70%' }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      {message.type === 'user' ? <FaUser className="me-2" /> : <FaRobot className="me-2" />}
                      <small className="fw-bold">
                        {message.type === 'user' ? 'You' : agents.find(a => a.id === message.agentId)?.name}
                      </small>
                      <small className="ms-auto text-muted">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </small>
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{message.text}</div>
                    {message.metadata && (
                      <div className="mt-2 pt-2 border-top">
                        <small className="text-muted">
                          {message.metadata.processingTime && `Processing time: ${message.metadata.processingTime}ms`}
                          {message.metadata.confidence && ` • Confidence: ${message.metadata.confidence}%`}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            
            {isLoading && (
              <div className="d-flex justify-content-start mb-3">
                <div className="bg-white border p-3 rounded">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span className="text-muted">Agent is typing...</span>
                </div>
              </div>
            )}
          </div>

          {suggestions.length > 0 && (
            <div className="p-2 border-top bg-light">
              <small className="text-muted">Suggestions:</small>
              <div className="d-flex gap-2 mt-1 flex-wrap">
                {suggestions.map((suggestion, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="p-3 border-top">
            <InputGroup>
              <Form.Control
                placeholder="Type your message..."
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                disabled={isLoading}
              />
              <Button 
                variant="primary" 
                onClick={sendMessage}
                disabled={!currentMessage.trim() || isLoading}
              >
                <FaPaperPlane />
              </Button>
            </InputGroup>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default AgentChat;